import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Download, Inbox, LayoutList, BarChart3, Tag, MapPin, Loader2 } from 'lucide-react';
import { adminApi, apiError, cleanParams } from '../../lib/adminApi';
import { LeadFilters } from '../../components/admin/LeadFilters';
import { LeadCard } from '../../components/admin/LeadCard';
import { LeadDetail } from '../../components/admin/LeadDetail';
import { LeadReports } from '../../components/admin/LeadReports';
import { StatCard, Panel, BarList, RefreshedAt, Change } from '../../components/admin/AdminUI';

const INITIAL = { date: { preset: 'all', start: '', end: '' }, q: '', service: 'all', city: 'all', channel: 'all', stage: 'all', owner: 'all', review: 'all', property_type: 'all', view: 'all' };

const VIEW_PARAMS = {
  all: { flag: 'clean' },
  new: { flag: 'clean', review: 'new' },
  followup: { flag: 'clean', followup: 'due' },
  flagged: { flag: 'flagged' },
};

const useDebounced = (value, ms) => {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
};

const Tabs = ({ tab, setTab }) => (
  <div className="inline-flex rounded-full border border-slate-200 bg-white p-1 shadow-sm" data-testid="leads-tabs">
    {[['inbox', 'Inbox', LayoutList], ['reports', 'Reports', BarChart3]].map(([k, label, Icon]) => (
      <button key={k} onClick={() => setTab(k)} data-testid={`leads-tab-${k}`}
        className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${tab === k ? 'bg-[#0E1F3D] text-white' : 'text-slate-600 hover:text-slate-900'}`}>
        <Icon className="h-4 w-4" />{label}
      </button>
    ))}
  </div>
);

export default function AdminLeads() {
  const [filters, setFilters] = useState(INITIAL);
  const [tab, setTab] = useState('inbox');
  const [options, setOptions] = useState(null);
  const [data, setData] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const q = useDebounced(filters.q, 300);

  const params = useMemo(() => cleanParams({
    start: filters.date.start, end: filters.date.end, q, service: filters.service, city: filters.city,
    channel: filters.channel, stage: filters.stage, owner: filters.owner, review: filters.review,
    property_type: filters.property_type, ...VIEW_PARAMS[filters.view],
  }), [filters, q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, rep] = await Promise.all([
        adminApi.get('/admin/leads', { params }),
        adminApi.get('/admin/leads/report', { params }),
      ]);
      setData(list.data);
      setReport(rep.data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [params]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { adminApi.get('/admin/leads/options').then((r) => setOptions(r.data)).catch(() => {}); }, [data?.generated_at]);

  const onUpdated = (lead) => setData((d) => d && ({ ...d, items: d.items.map((i) => (i.id === lead.id ? { ...i, ...lead } : i)) }));

  const markReviewed = async (lead) => {
    try {
      const { data: updated } = await adminApi.patch(`/admin/leads/${lead.id}`, { reviewed: true });
      onUpdated(updated);
      toast.success(`${lead.name} marked reviewed`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const exportCsv = async () => {
    try {
      const res = await adminApi.get('/admin/leads/export.csv', { params, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = (res.headers['content-disposition'] || '').match(/filename="(.+)"/)?.[1] || 'leads.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const t = report?.totals;
  return (
    <div className="space-y-6" data-testid="admin-leads-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-oswald text-3xl uppercase text-[#0E1F3D] sm:text-4xl">Lead Inbox</h1>
          <p className="mt-1 text-sm text-slate-500">Every website form submission, newest first.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Tabs tab={tab} setTab={setTab} />
          <button onClick={exportCsv} className="inline-flex h-10 items-center gap-2 rounded-full border border-slate-200 bg-white px-4 text-sm font-semibold shadow-sm hover:border-[#14366C]" data-testid="export-csv-btn">
            <Download className="h-4 w-4" />CSV
          </button>
          <RefreshedAt at={data?.generated_at} onRefresh={load} loading={loading} testId="leads-refresh-btn" />
        </div>
      </div>

      <LeadFilters filters={filters} setFilters={setFilters} options={options} counts={data?.view_counts} />

      {tab === 'reports' ? <LeadReports report={report} /> : (
        <>
          <div className="grid gap-4 md:grid-cols-3">
            <StatCard label="Leads in view" value={t?.leads} Icon={Inbox} testId="summary-total" sub={<Change pct={t?.change_pct} testId="summary-total-change" />} />
            <Panel title="Top services" Icon={Tag} testId="summary-top-services"><BarList items={report?.by_service.slice(0, 5)} testId="summary-top-services-list" /></Panel>
            <Panel title="Top cities" Icon={MapPin} testId="summary-top-cities"><BarList items={report?.by_city.slice(0, 5)} testId="summary-top-cities-list" /></Panel>
          </div>
          <div className="space-y-3" data-testid="lead-list">
            {loading && !data && <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#14366C]" /></div>}
            {data && data.items.length === 0 && (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center text-sm text-slate-500" data-testid="lead-list-empty">No leads match these filters.</div>
            )}
            {data?.items.map((lead) => <LeadCard key={lead.id} lead={lead} onOpen={(l) => setOpenId(l.id)} onMarkReviewed={markReviewed} />)}
            {data && data.total > data.items.length && <p className="text-center text-xs text-slate-500">Showing {data.items.length} of {data.total}. Narrow the filters or export CSV for the full list.</p>}
          </div>
        </>
      )}
      <LeadDetail leadId={openId} owners={options?.owners || []} onClose={() => setOpenId(null)} onUpdated={onUpdated} />
    </div>
  );
}
